import type { ServiceItem, ServiceTransaction } from '../../types';
import { isHourlyUnit } from '../serviceUnits';
import { isNepService } from '../nepPosition';
import { isFlatRateService } from '../flatRateLogic';
import { hatBestandteile } from '../bestandteile/bestandteilLogic';
import { rundeViertelstunde } from './unterweisungBooking';

/**
 * Die Buchung des Zeitaufwands einer GEFAEHRDUNGSBEURTEILUNG auf eine
 * LV-Position — als pure Funktion neben der Oberflaeche (Muster
 * unterweisungBooking.ts und einweisungBooking.ts, Plan §10).
 *
 * WARUM PURE: kein DOM in den Tests, und ein Fehler in diesem Pfad kostet
 * Geld. Die Funktion liest und schreibt nichts; sie gibt die NEUE
 * Leistungsliste und den Log-Eintrag zurueck. Der Aufrufer (GBU-Modul) geht
 * in DIESER Reihenfolge vor:
 *   0. `rechneGbuBuchung` als Probe — null heisst: nichts schreiben.
 *   1. Riegel zuerst: `markiereTaetigkeitGebucht` (RPC
 *      sifa_taetigkeit_gebucht_markieren) — sie liefert die Ids, die
 *      TATSAECHLICH markiert wurden. Leer → nichts gutschreiben.
 *   2. Leistungsliste + Log-Eintrag ueber `onUpdate` ins Projekt.
 *   3. `queueJournalMirror({ …, source: 'gbu', statements: { isAddon: false,
 *      coversInspection: false } })`.
 * Nie still: der Dialog bietet „ohne Leistungsbuchung" an.
 *
 * FACHLICH (Plan §10, E7): wie bei SiGeKo eine Stundenposition im LV. Die
 * Menge sind Stunden in Viertelstunden, mindestens 0,25 — eine Buchung mit 0
 * im Protokoll sieht aus wie eine und ist keine. Kein `bookedAt`: in
 * Altdaten heisst das „abgerechnet".
 */

export type ServiceLogEntry = ServiceTransaction;

// ─── Positionen ─────────────────────────────────────────────────────────────

/**
 * Die Zuordnung der GBU-Position. Verglichen wird als `string`: der Wert
 * `'gbu'` kam mit Paket G in den Typ `ServiceAssignment` (types.ts) — so
 * kompiliert der Vergleich vorher wie nachher, ohne „no overlap"-Fehler.
 */
const GBU_ZUORDNUNG = 'gbu';

/** Kleinbuchstaben, ohne diakritische Zeichen (ä → a), ß → ss. */
const normalisiereTitel = (titel: string | null | undefined): string =>
  (titel || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss');

/**
 * Traegt der Titel ein Stichwort der GBU (Entscheid O3)?
 *   • „Gefährdungsbeurteilung" in jeder Beugung und Schreibweise
 *     (Gefaehrdungs…, Gefahrdungs… aus umlautlosen Importen),
 *   • „GBU" als eigenes Wort (auch „GBUs") — als Teilwort steckt es z. B. in
 *     „FluGBUchung",
 *   • „Arbeitsschutzdokumentation" (Sammelposition).
 */
export const istGbuStichwort = (titel: string | null | undefined): boolean => {
  const t = normalisiereTitel(titel);
  if (!t.trim()) return false;
  return t.includes('gefahrdungsbeurteilung')
    || t.includes('gefaehrdungsbeurteilung')
    || t.includes('arbeitsschutzdokumentation')
    || /\bgbus?\b/.test(t);
};

/**
 * Kann auf diese Position ueberhaupt gebucht werden? Nie auf eine
 * PAKETPOSITION (Bestandteile, Kernregel 7 — sie bucht nur ihr
 * Abschlussschritt), nie auf eine NEP-Position (nicht beauftragt, braucht
 * eine Begruendung, die dieser Dialog nicht fragt) und nie auf eine
 * MONATSPAUSCHALE (dort waere eine Menge ein Monat Geld ohne
 * Monatsschluessel). Dieselbe Menge wie billingSelection.alleBuchbarenPositionen.
 */
const istBuchbar = (s: ServiceItem | null | undefined): s is ServiceItem =>
  !!s && !hatBestandteile(s) && !isNepService(s) && !isFlatRateService(s);

/**
 * Ist die Position die GBU-Position im engeren Sinn — die Gruppe, aus der
 * die Oberflaeche vorauswaehlt?
 *
 * Zweistufig wie waehleUnterweisungsPositionen bzw. selectBillableServices:
 *   1. ZUORDNUNG `gbu` — die fachliche Wahrheit, egal wie Titel und Einheit
 *      lauten (auch eine ausdruecklich zugeordnete Stueckposition).
 *   2. OHNE Zuordnung (`none`, fehlend oder null) mit Stichwort im Titel.
 * Eine ausdrueckliche FREMDE Zuordnung sperrt den Textarm: „Mitwirkung an
 * der Gefährdungsbeurteilung" mit `inspection` ist eine SiGeKo-Leistung und
 * wird hier nicht vorausgewaehlt. Paket-, NEP- und Pauschalpositionen nie.
 */
export const istGbuPassend = (s: ServiceItem | null | undefined): boolean => {
  if (!istBuchbar(s)) return false;
  const zuordnung = s.assignment as string | null | undefined;
  if (zuordnung === GBU_ZUORDNUNG) return true;
  if (zuordnung !== undefined && zuordnung !== null && zuordnung !== 'none') return false;
  return istGbuStichwort(s.title);
};

/**
 * Darf eine Stundenposition OHNE GBU-Bezug angeboten werden? Nur, wenn sie
 * keinem anderen Modul gehoert: ohne Zuordnung (`none`, fehlend, null) oder
 * als Zusatzleistung (`additional`). Positionen mit Modul-Zuordnung
 * (`inspection`, `unterweisung`, `einweisung`, `vorank`, `unterlage`,
 * `sigeplan`, `asplan`, `site_rules`, `planning_general`, `flatrate` …)
 * bleiben draussen — sonst landete GBU-Zeit aus Versehen im Begehungs- oder
 * Unterweisungszaehler (Controller-Entscheid 20.10.2026).
 */
const istFreieStundenposition = (s: ServiceItem): boolean => {
  if (!isHourlyUnit(s.unit)) return false;
  const zuordnung = s.assignment as string | null | undefined;
  return zuordnung === undefined || zuordnung === null || zuordnung === 'none' || zuordnung === 'additional';
};

/**
 * Welche LV-Positionen der Dialog „Zeit buchen" anbietet:
 *   1. zuerst die PASSENDEN (`istGbuPassend`),
 *   2. danach die uebrigen buchbaren STUNDENpositionen (`isHourlyUnit`), die
 *      keinem anderen Modul gehoeren — ohne Zuordnung oder `additional`
 *      (`istFreieStundenposition`).
 * Je Gruppe in der Reihenfolge des LV, jede Position nur einmal. Paket-,
 * NEP- und Pauschalpositionen bleiben immer draussen (`istBuchbar`);
 * Stueckpositionen kommen nur ueber Gruppe 1 hinein — eine Stunde auf einer
 * fremden Stueckposition waere ein Stueck.
 *
 * WARUM ueberhaupt Gruppe 2 (Betreiberwunsch 20.10.2026): jede GBU-Zeit muss
 * in Leistung & Budget landen koennen. Ein SiFa-LV fuehrt die GBU oft nicht
 * als eigene Position, sondern rechnet sie ueber „Sicherheitstechnische
 * Betreuung, Std." ab — der enge Filter lieferte dort eine leere Liste, und
 * „Zeit buchen" landete still nur im Taetigkeitsnachweis. Die Gruppe 2 ist
 * ein ANGEBOT, keine Vorauswahl: die Oberflaeche waehlt nur aus Gruppe 1
 * vor, sonst entscheidet der Mensch.
 */
export const waehleGbuPositionen = (services: ReadonlyArray<ServiceItem> | null | undefined): ServiceItem[] => {
  const buchbar = (services ?? []).filter(istBuchbar);
  const gesehen = new Set<string>();
  const einmal = (s: ServiceItem): boolean => {
    if (gesehen.has(s.id)) return false;
    gesehen.add(s.id);
    return true;
  };
  const passend = buchbar.filter((s) => istGbuPassend(s) && einmal(s));
  const uebrige = buchbar.filter((s) => !istGbuPassend(s) && istFreieStundenposition(s) && einmal(s));
  return [...passend, ...uebrige];
};

// ─── Menge ──────────────────────────────────────────────────────────────────

/** Viertelstunde — die kleinste Buchungsmenge. */
export const MINDESTMENGE_STUNDEN = 0.25;

/**
 * Stunden auf Viertelstunden (kaufmaennisch), mindestens 0,25, sobald
 * ueberhaupt Zeit vorliegt; 0 nur bei keiner oder unbrauchbarer Eingabe —
 * der Aufrufer bucht dann nicht.
 */
export const gbuStunden = (stunden: number | null | undefined): number => {
  const h = Number(stunden);
  if (!Number.isFinite(h) || h <= 0) return 0;
  return Math.max(MINDESTMENGE_STUNDEN, rundeViertelstunde(h));
};

/** Minuten (z. B. `sifa_taetigkeiten.dauer_minuten`) als buchbare Stunden. */
export const stundenAusMinuten = (minuten: number | null | undefined): number => {
  const m = Number(minuten);
  return Number.isFinite(m) && m > 0 ? gbuStunden(m / 60) : 0;
};

// ─── Buchung ────────────────────────────────────────────────────────────────

/** Was der Log-Text ueber die gebuchte GBU-Zeit sagen soll. */
export interface GbuBuchungsKontext {
  /** Anzeigename des Buchenden; ohne ihn steht „System" im Protokoll. */
  performerName?: string;
  /** Tag der Taetigkeit, ISO `yyyy-mm-dd` — Teil des `bookingReference`. */
  datum: string;
  /** Laufende Nummer der GBU je Projekt (SifaGbu.nummer). */
  gbuNummer: number;
  gbuTitel: string;
  /** Fassung, an der gearbeitet wurde (SifaGbu.fassung) — nur Text. */
  fassung?: number | null;
  /**
   * Id der Taetigkeit (`sifa_taetigkeiten.id`), die gebucht wird. Daraus
   * entsteht die STABILE Id des Log-Eintrags (gbuZeitLogId): geht nach dem
   * Riegel die Antwort verloren, laesst sich am serviceLog ablesen, ob die
   * Gutschrift schon im Projekt steht — und ein Nachholen schreibt dieselbe
   * Id, nie eine zweite.
   */
  taetigkeitId: string;
}

export interface GbuBuchung {
  /** Die vollstaendige Leistungsliste MIT der gebuchten Position. */
  services: ServiceItem[];
  /** Der Eintrag, der an `globalData.serviceLog` angehaengt wird. */
  logEntry: ServiceLogEntry;
  /** Gebuchte Menge — dieselbe Zahl wie logEntry.quantity (fuer den Riegel `p_menge`). */
  menge: number;
  /** Position rechnet in Stunden ab. */
  stundenposition: boolean;
}

/** `yyyy-mm-dd` → `yyyymmdd`; sonst die Ziffern des Textes — per Zeichenkette, nie ueber new Date. */
const datumKompakt = (iso: string | null | undefined): string => {
  const text = (iso || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  return m ? `${m[1]}${m[2]}${m[3]}` : text.replace(/\D/g, '');
};

/** ISO-Datum in deutsche Schreibweise, sonst wortgetreu. */
const formatiereDatum = (iso: string | null | undefined): string => {
  const text = (iso || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : text;
};

/**
 * Buchungskennung `GBU-<nummer>-<yyyymmdd>`: WELCHE GBU an WELCHEM Tag.
 * Journal und Leistungsnachweis erkennen sie am Praefix. Kein `reportRef`:
 * eine GBU ist kein Begehungsbericht.
 */
export const gbuBookingReference = (nummer: number, datumIso: string): string =>
  `GBU-${nummer}-${datumKompakt(datumIso)}`;

/** Praefix der Log-Eintraege aus „Zeit buchen". */
export const GBU_ZEIT_LOG_PRAEFIX = 'gbu-zeit-';

/**
 * Stabile Id des Log-Eintrags je Taetigkeit: `gbu-zeit-<taetigkeitId>`.
 * Deterministisch statt crypto.randomUUID — eine Taetigkeit wird genau einmal
 * gutgeschrieben, und ob das geschehen ist, steht am serviceLog.
 *
 * VERBINDLICH fuer jeden Abrechnungsweg von `sifa_taetigkeiten` (auch einen
 * kuenftigen im Taetigkeitsnachweis, Stufe 3): dieselbe Id verwenden. Der
 * Abgleich „Gutschrift fehlt" (components/sifa/gbu/gbuModulLogic.gbuZeitStand)
 * und die Serverpruefung vor dem Nachholen suchen genau diese Kennung —
 * eine andere Id ergaebe ein falsches „Gutschrift fehlt" und eine
 * Doppelbuchung beim Nachholen.
 */
export const gbuZeitLogId = (taetigkeitId: string): string => `${GBU_ZEIT_LOG_PRAEFIX}${(taetigkeitId || '').trim()}`;

/**
 * Protokolltext: Titel, Nummer, Fassung, Tag — dann wie im Bestand die Menge
 * mit Einheit. Der Freitext der Taetigkeit steht bewusst NICHT darin: die
 * Beschreibung wird an mehreren Stellen als Regel gelesen
 * (serviceLogUtils: „manuelle Verbuchung", „Bericht Nr. …"), ein frei
 * getippter Satz koennte die Buchung dort umdeuten. Er steht im
 * Taetigkeitsnachweis.
 */
const buildDescription = (kontext: GbuBuchungsKontext, menge: number, unit: string): string => {
  const teile: string[] = [`Nr. ${kontext.gbuNummer}`];
  if (typeof kontext.fassung === 'number' && kontext.fassung > 0) teile.push(`Fassung ${kontext.fassung}`);
  const datum = formatiereDatum(kontext.datum);
  if (datum) teile.push(datum);
  const titel = (kontext.gbuTitel || '').trim() || 'ohne Titel';
  return `Gefährdungsbeurteilung „${titel}" (${teile.join(', ')}) (${menge} ${unit})`;
};

/**
 * Rechnet die Buchung von `menge` auf die Position `positionId`.
 *
 * MENGE: bei einer Stundenposition die Stunden auf Viertelstunden, mindestens
 * 0,25. Bei einer anderen Einheit (eine ausdruecklich auf `gbu` gestellte
 * Stueckposition) die Zahl aus dem Dialog unveraendert — dort liefert
 * BillingSelectionModal 1 bzw. die Menge der Aufteilung.
 *
 * `null` — der Aufrufer darf dann NICHT schreiben —, wenn
 *   • keine `taetigkeitId` im Kontext steht (ohne sie keine stabile Id),
 *   • es die Position nicht (mehr) gibt,
 *   • sie Paket-, NEP- oder Pauschalposition ist (zweite Reihe hinter
 *     waehleGbuPositionen: eine gemerkte Auswahl soll nicht daran vorbei),
 *   • die Menge 0, negativ oder keine Zahl ist. Anders als bucheEinweisung
 *     faellt sie NICHT auf 1 zurueck: eine erfundene Stunde ist schlimmer als
 *     keine Buchung.
 *
 * STATUS: `done`, sobald das Kontingent erreicht oder ueberschritten ist —
 * die Regel des Bestands; ein Ueberschreiten wird nicht gedeckelt.
 */
export const rechneGbuBuchung = (
  services: ReadonlyArray<ServiceItem> | null | undefined,
  positionId: string,
  menge: number,
  kontext: GbuBuchungsKontext,
): GbuBuchung | null => {
  // Ohne Taetigkeit keine stabile Id — dann lieber gar nicht buchen.
  if (!(kontext?.taetigkeitId || '').trim()) return null;
  const liste = [...(services ?? [])];
  const index = liste.findIndex((s) => s?.id === positionId);
  if (index === -1) return null;
  const service = liste[index];
  if (!istBuchbar(service)) return null;

  const stundenposition = isHourlyUnit(service.unit);
  const roh = Number(menge);
  if (!Number.isFinite(roh) || roh <= 0) return null;
  const gebucht = stundenposition ? gbuStunden(roh) : roh;
  const unit = stundenposition ? (service.unit || 'Stunden') : (service.unit || 'Stück');

  const newUsed = (service.quantityUsed || 0) + gebucht;
  const isDone = service.quantityContracted > 0 && newUsed >= service.quantityContracted;
  liste[index] = {
    ...service,
    quantityUsed: newUsed,
    status: isDone ? 'done' : service.status,
  };

  const logEntry: ServiceLogEntry = {
    id: gbuZeitLogId(kontext.taetigkeitId),
    date: new Date().toISOString(),
    serviceId: service.id,
    quantity: gebucht,
    performerId: 'system',
    performerName: kontext.performerName || 'System',
    isSubstitution: false,
    description: buildDescription(kontext, gebucht, unit),
    bookingReference: gbuBookingReference(kontext.gbuNummer, kontext.datum),
  };

  return { services: liste, logEntry, menge: gebucht, stundenposition };
};
